// Builds the `cssf` CV variant as a docx Document, mirroring CssfPdf.jsx
// (header, user information, experiences, diplomas, languages, business skills,
// footer). Verdana throughout, strict 30/70 columns, teal accent (#009999).

import { Document, LineRuleType } from "docx";
import { extractResumeDataDocumentSection } from "../../../hooks/useResumeDocumentSection";
import { extractResumeProperties } from "../../../hooks/useResumeProperties";
import { loadImage } from "../shared/wordImage";
import { para, separateTables } from "../shared/wordHelpers";
import { FONT_VERDANA } from "../shared/wordTheme";
import { A4, ptToHalfPoints, pxToTwips } from "../shared/wordUnits";
import { buildCssfHeader } from "./sections/cssfHeader";
import { buildCssfFooter } from "./sections/cssfFooter";
import { buildCssfUserInformation } from "./sections/cssfUserInformation";
import { buildCssfExperiences } from "./sections/cssfExperiences";
import { buildCssfDiplomas } from "./sections/cssfDiplomas";
import { buildCssfLanguages } from "./sections/cssfLanguages";
import { buildCssfBusinessSkills } from "./sections/cssfBusinessSkills";

export async function buildCssfWord( resumeData, translations ) {
    const data = extractResumeDataDocumentSection( resumeData );
    const props = extractResumeProperties( resumeData );

    const [ logoImage, footerImage ] = await Promise.all( [
        loadImage( props.logoSrc ),
        loadImage( "/cssf-footer.png" ),
    ] );

    const ctx = { data, props, translations };
    const spacer = () => para( [], { spacing: { before: pxToTwips( 10 ), after: 0 } } );

    const children = [];
    children.push( ...buildCssfUserInformation( ctx ) );
    children.push( spacer(), ...buildCssfExperiences( ctx ) );
    children.push( spacer(), ...buildCssfDiplomas( ctx ) );
    children.push( spacer(), ...buildCssfLanguages( ctx ) );
    children.push( spacer(), ...buildCssfBusinessSkills( ctx ) );

    return new Document( {
        styles: {
            default: {
                document: {
                    run: { font: FONT_VERDANA, size: ptToHalfPoints( 10 ) },
                    paragraph: { spacing: { after: 0, line: 240, lineRule: LineRuleType.AUTO } },
                },
            },
        },
        sections: [
            {
                properties: {
                    page: {
                        size: { width: A4.width, height: A4.height },
                        margin: {
                            top: pxToTwips( 110 ),
                            right: pxToTwips( 40 ),
                            bottom: pxToTwips( 120 ),
                            left: pxToTwips( 40 ),
                            header: pxToTwips( 40 ),
                            footer: pxToTwips( 20 ),
                        },
                    },
                },
                headers: { default: buildCssfHeader( { logoImage } ) },
                footers: { default: buildCssfFooter( { footerImage } ) },
                children: separateTables( children ),
            },
        ],
    } );
}
